// src/guards/AdminRoute.tsx
// Admin route guard — extends ProtectedRoute with an admin role check.
// Redirects non-admin users to /dashboard.

import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import { supabase } from '@/src/lib/supabase'

interface AdminRouteProps {
  children: ReactNode
}

export function AdminRoute({ children }: AdminRouteProps) {
  const [loading, setLoading] = useState(true)
  const [authenticated, setAuthenticated] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const location = useLocation()

  useEffect(() => {
    async function checkAuth() {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session) {
        setAuthenticated(false)
        setLoading(false)
        return
      }

      setAuthenticated(true)

      // Check admin status from users table
      const { data: userRow } = await supabase
        .from('users')
        .select('is_admin')
        .eq('id', session.user.id)
        .single()

      setIsAdmin(userRow?.is_admin === true)
      setLoading(false)
    }

    checkAuth()
  }, [])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
      </div>
    )
  }

  if (!authenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}
